import { getOfficialRates, CBNRate } from './cbn-api';
import { getExchangeRates } from './api/index';
import { rateCache } from './cache';
import { ExchangeRate } from '../../types/currency';

export interface HybridRateResult {
  official: CBNRate[];
  parallel: ExchangeRate[];
  source: 'api' | 'fallback';
  aiValidated: boolean;
  lastUpdated: Date;
}

// Per-currency parallel market premium above the CBN official rate (October 2026)
// Source: AbokiFX, Monierate, NgnRates — parallel "buy" rate vs CBN official
const PARALLEL_PREMIUMS: Record<string, number> = {
  USD: 1.027,  // ~2.7% above official
  EUR: 1.035,  // ~3.5% above official
  GBP: 1.078,  // ~7.8% above official
  CAD: 1.046,  // ~4.6% above official
  AUD: 1.021,  // ~2.1% above official
  XOF: 1.035,  // ~3.5% above official
};

const DEFAULT_PARALLEL_PREMIUM = 1.04;

// October 2026 fallback base rates (used when both APIs fail)
const FALLBACK_BASE_RATES = [
  { currency: 'USD', officialBase: 1331.50, parallelBase: 1368.00, symbol: '$' },
  { currency: 'EUR', officialBase: 1515.51, parallelBase: 1569.00, symbol: '€' },
  { currency: 'GBP', officialBase: 1762.40, parallelBase: 1900.00, symbol: '£' },
  { currency: 'CAD', officialBase: 975.00, parallelBase: 1020.00, symbol: 'C$' },
  { currency: 'AUD', officialBase: 950.00, parallelBase: 970.00, symbol: 'A$' },
  { currency: 'XOF', officialBase: 2.31, parallelBase: 2.39, symbol: 'CFA' },
];

function generateRealisticRates(): { official: CBNRate[], parallel: ExchangeRate[] } {
  const variance = () => 1 + (Math.random() - 0.5) * 0.006;

  const fallbackOfficial: CBNRate[] = FALLBACK_BASE_RATES.map(r => {
    const central = r.officialBase * variance();
    return {
      currency: r.currency,
      buyingRate: central * 0.997,
      sellingRate: central * 1.003,
      centralRate: central,
      lastUpdated: new Date(),
    };
  });

  const fallbackParallel: ExchangeRate[] = FALLBACK_BASE_RATES.map(r => ({
    fromCurrency: r.currency,
    toCurrency: 'NGN',
    rate: r.parallelBase * variance(),
    symbol: r.symbol,
    timestamp: new Date(),
    change: (Math.random() - 0.5) * 3,
  }));

  return { official: fallbackOfficial, parallel: fallbackParallel };
}

async function fetchFromTraditionalAPIs(): Promise<{ official: CBNRate[], parallel: ExchangeRate[] }> {
  const [official, parallel] = await Promise.all([
    getOfficialRates(),
    getExchangeRates(),
  ]);
  return { official, parallel };
}

export async function getHybridRates(useAI: boolean = true): Promise<HybridRateResult> {
  const cacheKey = 'hybrid-rates';
  const cached = rateCache.get(cacheKey);

  if (cached) {
    console.log('Using cached hybrid rates');
    return cached;
  }

  try {
    console.log('Fetching live rates from traditional APIs...');

    const apiRates = await fetchFromTraditionalAPIs();

    // Build official rate lookup from CBN data
    const officialByCurrency = new Map(apiRates.official.map(rate => [rate.currency, rate]));

    // International APIs return mid-market rates which are closest to the CBN official rate.
    // We treat those as the "official" basis and compute parallel rates by applying
    // the per-currency premium that reflects the actual Nigerian street/parallel market.
    const official: CBNRate[] = apiRates.parallel.map(rate => {
      const existing = officialByCurrency.get(rate.fromCurrency);
      const centralRate = existing && existing.centralRate > 0
        ? existing.centralRate
        : rate.rate; // If no CBN data, treat the mid-market rate as official

      return {
        currency: rate.fromCurrency,
        buyingRate: centralRate * 0.998,
        sellingRate: centralRate * 1.002,
        centralRate,
        lastUpdated: existing?.lastUpdated ?? new Date(),
      };
    });

    // Enforce invariant: parallel must always be higher than official
    const parallel: ExchangeRate[] = apiRates.parallel.map(rate => {
      const officialRate = officialByCurrency.get(rate.fromCurrency)?.centralRate ?? rate.rate;
      const premium = PARALLEL_PREMIUMS[rate.fromCurrency] ?? DEFAULT_PARALLEL_PREMIUM;
      const minParallel = officialRate * premium;
      // Use the higher of: the API-returned rate or the premium-derived rate
      const parallelRate = Math.max(rate.rate, minParallel);

      return {
        ...rate,
        rate: Number(parallelRate.toFixed(4)),
      };
    });

    const result: HybridRateResult = {
      official,
      parallel,
      source: 'api',
      aiValidated: false,
      lastUpdated: new Date(),
    };

    rateCache.set(cacheKey, result, 60000);
    console.log(`✓ Rates from traditional APIs - Source: api`);
    return result;
  } catch (error) {
    console.error('API fetch failed, using realistic fallback rates:', error);

    const { official, parallel } = generateRealisticRates();

    const result: HybridRateResult = {
      official,
      parallel,
      source: 'fallback',
      aiValidated: false,
      lastUpdated: new Date(),
    };

    rateCache.set(cacheKey, result, 300000);
    console.log('⚠ Using static fallback rates');
    return result;
  }
}

export async function refreshRatesWithAI(): Promise<HybridRateResult> {
  rateCache.clear();
  return getHybridRates(true);
}

export function clearRateCache(): void {
  rateCache.delete('hybrid-rates');
  console.log('Rate cache cleared - fresh data will be fetched on next request');
}
